'use client';

import { useEffect, useRef, useState } from 'react';
import { JetBrains_Mono } from 'next/font/google';
import { useTranslations } from 'next-intl';
import { COVENANT_TOPICS } from '@/data/covenant';

const covenantMono = JetBrains_Mono({
  subsets: ['latin'],
  weight: '700',
  display: 'swap',
  variable: '--font-covenant-mono',
});

function revealOpenedCovenantItem(
  item: HTMLLIElement,
  opts: { pinToStart: boolean; reduceMotion: boolean },
) {
  const behavior = opts.reduceMotion ? 'auto' : 'smooth';
  if (opts.pinToStart) {
    item.scrollIntoView({ behavior, block: 'start' });
    return;
  }

  const nav = document.querySelector('.cs-nav');
  const topBound =
    (nav instanceof HTMLElement ? nav.getBoundingClientRect().bottom : 0) + 16;
  const row = item.querySelector('.cs-covenant-row');
  const rowTop = (row instanceof HTMLElement ? row : item).getBoundingClientRect().top;
  const overflow = item.getBoundingClientRect().bottom - window.innerHeight;

  let delta = 0;
  if (rowTop < topBound) {
    delta = rowTop - topBound;
  } else if (overflow > 0) {
    delta = Math.min(overflow, Math.max(0, rowTop - topBound));
  }

  if (Math.abs(delta) < 1) return;
  window.scrollBy({ top: delta, behavior });
}

/**
 * Covenant topic rows: closed headers (Homepage POST-DRAFT 1572:16382)
 * plus the detached open tray (Homepage Coming NO LEGIONS Soon / DROPDOWN).
 * One panel open at a time. Panel copy is per topic under
 * `pages.home.cs.covenant.panels.<labelKey>` (Figma Accordion Copy).
 */
export function CovenantTopics() {
  const t = useTranslations('pages.home.cs.covenant');
  const [openId, setOpenId] = useState<string | null>(null);
  const itemRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const pendingScroll = useRef<'open' | 'close' | null>(null);
  const restoreY = useRef<number | null>(null);

  useEffect(() => {
    const action = pendingScroll.current;
    if (!action) return;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';

    const frame = window.requestAnimationFrame(() => {
      pendingScroll.current = null;

      if (action === 'open' && openId) {
        const node = itemRefs.current[openId];
        if (node) {
          revealOpenedCovenantItem(node, {
            pinToStart: isMobile,
            reduceMotion,
          });
        }
        return;
      }

      if (action === 'close' && restoreY.current != null) {
        window.scrollTo({ top: restoreY.current, behavior });
        restoreY.current = null;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [openId]);

  return (
    <ul className={`cs-covenant-list ${covenantMono.variable}`}>
      {COVENANT_TOPICS.map((item) => {
        const open = openId === item.id;
        const index = String(item.index).padStart(2, '0');
        const panelId = `covenant-panel-${item.id}`;
        const labelId = `covenant-label-${item.id}`;

        return (
          <li
            className={open ? 'cs-covenant-item is-open' : 'cs-covenant-item'}
            key={item.id}
            ref={(el) => {
              itemRefs.current[item.id] = el;
            }}
          >
            <button
              type="button"
              className="cs-covenant-row"
              aria-expanded={open}
              aria-controls={open ? panelId : undefined}
              onClick={() => {
                if (open) {
                  pendingScroll.current = 'close';
                  setOpenId(null);
                  return;
                }
                if (restoreY.current == null) {
                  restoreY.current = window.scrollY;
                }
                pendingScroll.current = 'open';
                setOpenId(item.id);
              }}
            >
              <span className="cs-covenant-emblem" aria-hidden="true">
                <img src="/cs/covenant-emblem-glyph.svg" alt="" width={15} height={15} />
              </span>
              <span className="cs-covenant-number" aria-hidden="true">
                {index}
              </span>
              <span className="cs-covenant-label" id={labelId}>
                {t(`items.${item.labelKey}`)}
              </span>
              <span className="cs-covenant-chevron" aria-hidden="true" />
            </button>
            {open ? (
              <div className="cs-covenant-panel" id={panelId} role="region" aria-labelledby={labelId}>
                <p className={item.points ? 'cs-covenant-copy-lead' : 'cs-covenant-copy'}>
                  {t(`panels.${item.labelKey}.body`)}
                </p>
                {item.points ? (
                  <>
                    <hr className="cs-covenant-rule-break" />
                    <ul className="cs-covenant-points">
                      {item.points.map((point) => (
                        <li className="cs-covenant-copy-lead" key={point}>
                          {t(`panels.${item.labelKey}.${point}`)}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {item.chain ? (
                  <p className="cs-covenant-copy">{t(`panels.${item.labelKey}.chain`)}</p>
                ) : null}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
